import { type ExternalToast, toast as sonnerToast } from 'sonner';

import { sanitizeUserFacingText } from '@kit/shared/upstream-error';

type ErrorMessage = Parameters<typeof sonnerToast.error>[0];

function presentErrorText<T>(message: T): T {
  if (typeof message !== 'string') {
    return message;
  }

  return sanitizeUserFacingText(message) as T;
}

/**
 * Captured before any reassignment. `sonnerToast` is one shared function
 * object, so writing `error` onto it would replace this method with the
 * wrapper, and the wrapper would call itself until the stack overflows.
 */
const showSonnerError = sonnerToast.error.bind(sonnerToast);

/**
 * Toasts are where a failed save shows its error. A gateway body must not
 * land in that sentence.
 */
function error(message: ErrorMessage, data?: ExternalToast) {
  const nextData =
    data && typeof data.description === 'string'
      ? { ...data, description: sanitizeUserFacingText(data.description) }
      : data;

  return showSonnerError(presentErrorText(message), nextData);
}

const toast: typeof sonnerToast = Object.assign(
  (
    message: Parameters<typeof sonnerToast>[0],
    data?: Parameters<typeof sonnerToast>[1],
  ) => sonnerToast(message, data),
  sonnerToast,
  { error },
);

export { toast };
